import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { openCalendlyPopup } from "@/components/CalendlyBooking";
import { CALENDLY_SESSIONS } from "@/data/content";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm tracking-wide font-medium transition-colors duration-300 focus-visible:outline-none";
const VARIANTS = {
  primary: "bg-cove-slate text-cove-cream hover:bg-cove-olive",
  secondary:
    "border border-cove-slate/40 text-cove-slate hover:border-cove-slate hover:bg-cove-slate/[0.04]",
  light: "bg-cove-cream text-cove-slate hover:bg-white",
  gold: "bg-cove-gold text-white hover:bg-[#b3893f]",
};

// Small dropdown that lets the user pick a session length before Calendly opens
function BookSessionDropdown({ variant, className, testId, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuWidth = Math.max(260, r.width);
    // Align menu's right edge with trigger's right edge; keep inside viewport
    let left = r.right - menuWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - 8 - menuWidth;
    setPos({ top: r.bottom + 12, left, width: menuWidth });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    const onReflow = () => updatePosition();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open]);

  const menu = open && typeof document !== "undefined"
    ? createPortal(
        <AnimatePresence>
          <motion.div
            key="book-session-menu"
            ref={menuRef}
            role="menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            style={{ top: pos.top, left: pos.left, minWidth: pos.width }}
            className="fixed rounded-2xl bg-cove-cream border border-cove-slate/15 shadow-[0_20px_60px_-25px_rgba(37,44,54,0.4)] p-2 z-[1000]"
            data-testid={testId ? `${testId}-menu` : undefined}
          >
            {CALENDLY_SESSIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                role="menuitem"
                data-testid={`${testId || "book"}-menu-${s.id}`}
                onClick={() => {
                  setOpen(false);
                  openCalendlyPopup(s.url);
                }}
                className="w-full text-left rounded-xl px-4 py-3 hover:bg-cove-slate/[0.06] transition-colors"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-serif text-lg text-cove-slate">{s.label}</span>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-cove-taupe">
                    {s.duration}
                  </span>
                </div>
              </button>
            ))}
          </motion.div>
        </AnimatePresence>,
        document.body
      )
    : null;

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <motion.span
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
          className={`${BASE} ${VARIANTS[variant]} ${className}`}
        >
          {children}
          <ChevronDown
            size={16}
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </motion.span>
      </button>
      {menu}
    </div>
  );
}

// Pill CTA. Routes internally by default; set `calendly` to open a session-picker dropdown.
// Pass `calendlyUrl` (string) instead of `calendly` to open a specific Calendly link directly.
export const CoveButton = ({
  to = "/contact",
  variant = "primary",
  children,
  className = "",
  testId,
  onClick,
  calendly = false,
  calendlyUrl,
}) => {
  if (calendly) {
    return (
      <BookSessionDropdown variant={variant} className={className} testId={testId}>
        {children}
      </BookSessionDropdown>
    );
  }

  if (calendlyUrl) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={() => openCalendlyPopup(calendlyUrl)}
        aria-label={typeof children === "string" ? children : undefined}
      >
        <motion.span
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
          className={`${BASE} ${VARIANTS[variant]} ${className}`}
        >
          {children}
        </motion.span>
      </button>
    );
  }

  const content = (
    <motion.span
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className={`${BASE} ${VARIANTS[variant]} ${className}`}
    >
      {children}
    </motion.span>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        aria-label={typeof children === "string" ? children : undefined}
      >
        {content}
      </button>
    );
  }

  return (
    <Link to={to} data-testid={testId}>
      {content}
    </Link>
  );
};

export default CoveButton;
